import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useCrawlNgrams, useCrawlDuplicates, useCrawlCannibalization, useCrawlClusters } from '../hooks/useCrawls'

interface ContentIntelligenceTabProps {
  crawlId: string
}

export function ContentIntelligenceTab({ crawlId }: ContentIntelligenceTabProps) {
  const [ngramSize, setNgramSize] = useState(2)
  const { data: ngramData, isLoading: ngramLoading } = useCrawlNgrams(crawlId, ngramSize, true)
  const { data: dupData, isLoading: dupLoading } = useCrawlDuplicates(crawlId, true)
  const { data: cannibalData, isLoading: cannibalLoading } = useCrawlCannibalization(crawlId, true)
  const { data: clusterData, isLoading: clusterLoading } = useCrawlClusters(crawlId, true)

  return (
    <div className="space-y-4">
      {/* Readability Distribution */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Readability Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          {dupLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : dupData?.readability ? (
            <div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                <ReadabilityCard label="Easy (60+)" count={dupData.readability.easy} color="text-green-600 dark:text-green-400" />
                <ReadabilityCard label="Moderate (30-59)" count={dupData.readability.moderate} color="text-yellow-600 dark:text-yellow-400" />
                <ReadabilityCard label="Difficult (<30)" count={dupData.readability.difficult} color="text-red-600 dark:text-red-400" />
                <ReadabilityCard label="Unscored" count={dupData.readability.unscored} color="text-muted-foreground" />
              </div>
              {dupData.readability.avgScore !== null && (
                <p className="text-sm text-muted-foreground">
                  Average Flesch-Kincaid score: <span className="font-medium text-foreground">{dupData.readability.avgScore}</span>
                  {' '}({readabilityLabel(dupData.readability.avgScore)})
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No readability data available. Readability scores are computed for pages with 30+ words on new crawls.</p>
          )}
        </CardContent>
      </Card>

      {/* N-gram Keyword Frequency */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Keyword Frequency</CardTitle>
            <div className="flex gap-1">
              {[1, 2, 3].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setNgramSize(n)}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                    ngramSize === n
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {n}-gram
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {ngramLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : ngramData?.ngrams && ngramData.ngrams.length > 0 ? (
            <div className="max-h-[400px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Phrase</TableHead>
                    <TableHead className="text-right w-24">Count</TableHead>
                    <TableHead className="w-40">Frequency</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ngramData.ngrams.slice(0, 50).map((item, idx) => {
                    const maxCount = ngramData.ngrams[0]?.count ?? 1
                    const pct = (item.count / maxCount) * 100
                    return (
                      <TableRow key={item.phrase}>
                        <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
                        <TableCell className="font-mono text-sm">{item.phrase}</TableCell>
                        <TableCell className="text-right">{item.count}</TableCell>
                        <TableCell>
                          <div className="h-2 bg-muted rounded overflow-hidden">
                            <div className="h-full bg-primary/60 rounded" style={{ width: `${pct}%` }} />
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No {ngramSize}-gram phrases found with 2+ occurrences.
            </p>
          )}
          {ngramData && (
            <p className="text-xs text-muted-foreground mt-2">
              Analysed {ngramData.totalTexts} text fields (titles, H1s, meta descriptions).
            </p>
          )}
        </CardContent>
      </Card>

      {/* Duplicate Content (Exact) */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Exact Duplicate Content</CardTitle>
            {dupData && dupData.totalDuplicatePages > 0 && (
              <Badge variant="destructive">
                {dupData.totalDuplicateGroups > dupData.duplicateGroups.length
                  ? `${dupData.duplicateGroups.length} of ${dupData.totalDuplicateGroups} groups`
                  : `${dupData.totalDuplicateGroups} groups`}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {dupLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : dupData?.duplicateGroups && dupData.duplicateGroups.length > 0 ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {dupData.totalDuplicatePages} pages across {dupData.totalDuplicateGroups} duplicate groups share identical body content.
                {dupData.totalDuplicateGroups > dupData.duplicateGroups.length && (
                  <> Showing top {dupData.duplicateGroups.length} by group size.</>
                )}
              </p>
              {dupData.duplicateGroups.map((group) => (
                <div key={group.contentHash} className="border rounded-md p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">{group.pageCount} identical pages</span>
                    <span className="text-xs text-muted-foreground font-mono">
                      {group.contentHash.slice(0, 12)}...
                    </span>
                  </div>
                  <div className="space-y-1">
                    {group.pages.map((page) => (
                      <div key={page.url} className="flex items-center gap-2 text-sm">
                        <span className="font-mono text-xs text-muted-foreground truncate flex-1 min-w-0" title={page.url}>
                          {new URL(page.url).pathname}
                        </span>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {page.word_count} words
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No exact duplicate content detected. Content hashes are computed on new crawls.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Near-Duplicate Content (SimHash) */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Near-Duplicate Content</CardTitle>
            {dupData?.nearDuplicateGroups && dupData.nearDuplicateGroups.length > 0 && (
              <Badge variant="secondary">{dupData.nearDuplicateGroups.length} groups</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {dupLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : dupData?.nearDuplicateGroups && dupData.nearDuplicateGroups.length > 0 ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {dupData.totalNearDuplicatePages} pages across {dupData.nearDuplicateGroups.length} groups have very similar content (SimHash distance &le; 3).
              </p>
              {dupData.nearDuplicateGroups.map((group) => (
                <div key={group.groupId} className="border rounded-md p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">{group.pageCount} similar pages</span>
                    <span className="text-xs text-muted-foreground">Group {group.groupId}</span>
                  </div>
                  <div className="space-y-1">
                    {group.pages.map((page) => (
                      <div key={page.url} className="flex items-center gap-2 text-sm">
                        <span className="font-mono text-xs text-muted-foreground truncate flex-1 min-w-0" title={page.url}>
                          {new URL(page.url).pathname}
                        </span>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {page.word_count} words
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No near-duplicate content detected. SimHash fingerprints are computed on new crawls for pages with 50+ characters of body text.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Keyword Cannibalization */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Keyword Cannibalization</CardTitle>
            {cannibalData?.groups && cannibalData.groups.length > 0 && (
              <Badge variant="secondary">{cannibalData.groups.length} groups</Badge>
            )}
          </div>
          {cannibalData && cannibalData.totalTitledPages > cannibalData.analysedPages && (
            <p className="text-xs text-muted-foreground mt-1">
              Analysed first {cannibalData.analysedPages.toLocaleString()} of {cannibalData.totalTitledPages.toLocaleString()} titled pages. Larger sets are not fully scanned for performance.
            </p>
          )}
        </CardHeader>
        <CardContent>
          {cannibalLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : cannibalData?.groups && cannibalData.groups.length > 0 ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Pages with overlapping title/H1 keywords that may compete for the same search queries.
              </p>
              {cannibalData.groups.map((group, idx) => (
                <div key={idx} className="border rounded-md p-3">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="text-sm font-medium">{group.pages.length} competing pages</span>
                    {group.sharedTerms.map((term) => (
                      <Badge key={term} variant="outline" className="text-xs">{term}</Badge>
                    ))}
                  </div>
                  <div className="space-y-1">
                    {group.pages.map((page) => (
                      <div key={page.url} className="text-sm">
                        <span className="font-mono text-xs text-muted-foreground truncate block min-w-0" title={page.url}>
                          {new URL(page.url).pathname}
                        </span>
                        <span className="text-xs text-muted-foreground">{page.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No keyword cannibalization detected. Pages are analysed for overlapping title and H1 keywords.
            </p>
          )}
        </CardContent>
      </Card>
      {/* Content Clusters (AI Embeddings) */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Content Clusters</CardTitle>
            {clusterData?.clusters && clusterData.clusters.length > 0 && (
              <Badge variant="secondary">{clusterData.clusters.length} clusters</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {clusterLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : clusterData?.clusters && clusterData.clusters.length > 0 ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Pages grouped by semantic similarity using AI embeddings. Pages in the same cluster cover similar topics and may benefit from internal linking or consolidation.
              </p>
              {clusterData.clusters.map((cluster) => (
                <details key={cluster.id} className="border rounded-md">
                  <summary className="p-3 cursor-pointer hover:bg-muted/50 flex items-center justify-between">
                    <span className="text-sm font-medium">Cluster {cluster.id + 1}</span>
                    <Badge variant="outline" className="text-xs">{cluster.count} pages</Badge>
                  </summary>
                  <div className="px-3 pb-3 space-y-1">
                    {cluster.pages.map((page) => (
                      <div key={page.url} className="flex items-center gap-2 text-sm">
                        <span className="font-mono text-xs text-muted-foreground truncate flex-1 min-w-0" title={page.url}>
                          {new URL(page.url).pathname}
                        </span>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {page.wordCount} words
                        </span>
                      </div>
                    ))}
                  </div>
                </details>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No content clusters available. Clusters are generated using AI embeddings on new crawls with 5+ pages of substantial content.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function ReadabilityCard({ label, count, color }: { label: string; count: number; color: string }) {
  return (
    <div className="text-center p-3 rounded-md bg-muted/50">
      <div className={`text-2xl font-bold ${color}`}>{count}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  )
}

function readabilityLabel(score: number): string {
  if (score >= 80) return 'Very easy'
  if (score >= 60) return 'Easy'
  if (score >= 40) return 'Moderate'
  if (score >= 20) return 'Difficult'
  return 'Very difficult'
}
